import {
  CodeBlock,
  CodeBlockCode,
} from "@arcadeai/design-system/components/ui/atoms/code-block";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import { useCallback, useMemo } from "react";
import { MCP_CLIENTS } from "../client-config";
import type { ConfigFormat, McpAuthType, McpClientId } from "../types";

type ConfigDisplayProps = {
  clientId: McpClientId;
  gatewayName: string;
  slug: string;
  authType: McpAuthType;
  enginePublicUrl: string;
  userId?: string;
  /** Optional host-supplied URL forwarded to a client's `renderFooter`. */
  apiKeysHref?: string;
  onCopy?: (
    clientId: McpClientId,
    format: ConfigFormat,
    authType: McpAuthType
  ) => void;
};

function getLanguageFromFormat(format: ConfigFormat): string {
  switch (format) {
    case "toml":
      return "toml";
    case "yaml":
      return "yaml";
    case "command":
      return "bash";
    case "text":
    case "url":
      return "text";
    default:
      return "json";
  }
}

export function ConfigDisplay({
  clientId,
  gatewayName,
  slug,
  authType,
  enginePublicUrl,
  userId,
  apiKeysHref,
  onCopy,
}: ConfigDisplayProps) {
  const client = MCP_CLIENTS[clientId];
  const format = client.format;
  const language = getLanguageFromFormat(format);

  const { config, error } = useMemo(() => {
    try {
      return {
        config: client.buildConfig({
          enginePublicUrl,
          gatewayName,
          slug,
          authType,
          userId,
        }),
        error: null,
      };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to build configuration";
      return { config: "", error: errorMessage };
    }
  }, [client, enginePublicUrl, gatewayName, slug, authType, userId]);

  const handleCopy = useCallback(() => {
    onCopy?.(clientId, format, authType);
  }, [onCopy, clientId, format, authType]);

  const handleFooterCopy = useCallback(
    (footerFormat: ConfigFormat) => {
      onCopy?.(clientId, footerFormat, authType);
    },
    [onCopy, clientId, authType]
  );

  if (error) {
    return (
      <div className="space-y-2">
        <div className="rounded-md border border-destructive bg-destructive/10 p-3">
          <p className="text-destructive text-sm">
            Failed to generate configuration: {error}
          </p>
        </div>
      </div>
    );
  }

  const footer = client.renderFooter?.({
    authType,
    userId,
    gatewayName,
    apiKeysHref,
    onConfigCopy: handleFooterCopy,
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm">
          {client.setupHint}
        </span>
        <CopyButton content={config} onCopy={handleCopy} variant="ghost" />
      </div>
      <CodeBlock>
        <CodeBlockCode
          className="[&_.shiki]:whitespace-pre-wrap [&_.shiki]:break-all"
          code={config}
          language={language}
        />
      </CodeBlock>
      {footer}
    </div>
  );
}
